import type {
  LynxBackgroundLocationStatus,
  LynxLocationAuthorizationResult,
  LynxHeadingUpdateEvent,
  LynxLocationUpdateEvent,
  LynxPublishConfig,
  LynxPublishEvent,
  LynxPublishStatus,
} from '../native/locationDefinitions';
import { LynxLocation } from '../native/LynxLocation';

type Unsubscribe = () => void | Promise<void>;

/** Injectable location bridge — production wraps LynxLocation; tests use a fake. */
export interface ProximityLocationBridge {
  getAuthorizationStatus(): Promise<LynxLocationAuthorizationResult>;
  requestWhenInUseAuthorization(): Promise<LynxLocationAuthorizationResult>;
  requestAlwaysAuthorization(): Promise<LynxLocationAuthorizationResult>;
  openAppSettings(): Promise<{ opened: boolean }>;
  startWatching(): Promise<{ watching: boolean }>;
  stopWatching(): Promise<{ watching: boolean }>;
  getBackgroundLocationStatus(): Promise<LynxBackgroundLocationStatus>;
  startPublishing(config: LynxPublishConfig): Promise<{ publishing: boolean }>;
  stopPublishing(): Promise<{ publishing: boolean }>;
  updatePublishTokens(tokens: {
    accessToken: string;
    refreshToken?: string;
  }): Promise<{ updated: boolean }>;
  getPublishStatus(): Promise<LynxPublishStatus>;
  addPublishEventListener(handler: (event: LynxPublishEvent) => void): Promise<Unsubscribe>;
  addLocationUpdateListener(
    handler: (event: LynxLocationUpdateEvent) => void,
  ): Promise<Unsubscribe>;
  addHeadingUpdateListener(handler: (event: LynxHeadingUpdateEvent) => void): Promise<Unsubscribe>;
  addLocationErrorListener(handler: (event: { message: string }) => void): Promise<Unsubscribe>;
  addAuthorizationChangedListener(
    handler: (event: LynxLocationAuthorizationResult) => void,
  ): Promise<Unsubscribe>;
}

export function createCapacitorLocationBridge(
  location: Pick<
    typeof LynxLocation,
    | 'getAuthorizationStatus'
    | 'requestWhenInUseAuthorization'
    | 'requestAlwaysAuthorization'
    | 'openAppSettings'
    | 'startWatching'
    | 'stopWatching'
    | 'getBackgroundLocationStatus'
    | 'startPublishing'
    | 'stopPublishing'
    | 'updatePublishTokens'
    | 'getPublishStatus'
    | 'addListener'
  > = LynxLocation,
): ProximityLocationBridge {
  return {
    getAuthorizationStatus() {
      return location.getAuthorizationStatus();
    },

    requestWhenInUseAuthorization() {
      return location.requestWhenInUseAuthorization();
    },

    requestAlwaysAuthorization() {
      return location.requestAlwaysAuthorization();
    },

    openAppSettings() {
      return location.openAppSettings();
    },

    startWatching() {
      return location.startWatching();
    },

    stopWatching() {
      return location.stopWatching();
    },

    getBackgroundLocationStatus() {
      return location.getBackgroundLocationStatus();
    },

    startPublishing(config) {
      return location.startPublishing(config);
    },

    stopPublishing() {
      return location.stopPublishing();
    },

    updatePublishTokens(tokens) {
      return location.updatePublishTokens(tokens);
    },

    getPublishStatus() {
      return location.getPublishStatus();
    },

    async addPublishEventListener(handler) {
      const handle = await location.addListener('publishEvent', handler);
      return async () => {
        await handle.remove();
      };
    },

    async addLocationUpdateListener(handler) {
      const handle = await location.addListener('locationUpdate', handler);
      return async () => {
        await handle.remove();
      };
    },

    async addHeadingUpdateListener(handler) {
      const handle = await location.addListener('headingUpdate', handler);
      return async () => {
        await handle.remove();
      };
    },

    async addLocationErrorListener(handler) {
      const handle = await location.addListener('locationError', handler);
      return async () => {
        await handle.remove();
      };
    },

    async addAuthorizationChangedListener(handler) {
      const handle = await location.addListener('authorizationChanged', handler);
      return async () => {
        await handle.remove();
      };
    },
  };
}
